const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class SampleSearch extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this.DefaultSearchFilters = {
			IDCustomer: null,
			Projects: [],
			Materials: [],
			MixDesigns: [],
			StartDate: '',
			EndDate: '',
			PageSize: 25,
			CurrentPage: 1,
			TotalPages: 1,
			SortBy: 'CreateDate',
			SortDirection: 'DESC',
		};
		this.CurrentSearchFilters = JSON.parse(JSON.stringify(this.DefaultSearchFilters));
		this.CurrentSearchFiltersCount = 0;
		this.CurrentSearchResults = [];
		// this is the active sample that is selected; will be one we see in the "Sample Details" panel, etc.
		this.selectedSample = {};

		// make the status a pretty little pill
		this.pict.MetaTemplate.addPattern('{~StatusPill:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpStatusValue = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			// get the status details, this will return a name, color, and icon for any status value
			const tmpStatus = this.pict.providers.SampleProvider.getSampleStatusMappedDetails(tmpStatusValue);
			const tmpDisplayTag =
			`
			<style>
				.is-unknown
				{
					background-color: transparent;
					border: 1px solid var(--bulma-text-70);
					/* the panels chrome is dark; without this the tag inherits .tag's dark text */
					color: var(--bulma-text-70);
				}
				.has-text-unkown
				{
					color: var(--bulma-text-70);
				}
			</style>
			<span class="tag is-rounded pl-1 ${tmpStatus?.color}">
				<span class="icon m-0 ${tmpStatus.iconColor}">
					<i class="fas fa-fw ${tmpStatus?.icon}"></i>
				</span>
				<span class="icon-text">${tmpStatus?.name}</span>
			</span>`;
			return tmpDisplayTag;
		});
	}

	onBeforeRenderAsync(pCallback)
	{
		this.CurrentSearchFilters.IDCustomer = this.AppData?.DocumentData?.ReportData?.IDCustomer || this.AppData?.CurrentUser?.CustomerID || 0;
		if (pCallback)
		{
			pCallback();
		};
	}

	onAfterRenderAsync(pCallback)
	{
		// PROJECT Selector Filter
		// if we have a project already, make it sticky on the top of the list
		const tmpProject = this.AppData?.DocumentData?.ReportData?.IDProject ? { id: this.AppData?.DocumentData?.ReportData?.IDProject, text: this.AppData?.DocumentData?.ReportData?.Project?.Name } : null;
		this.initializeSelect2Plugin({
			IDCustomer: this.CurrentSearchFilters?.IDCustomer,
			dropdownCssClass: 'theme-dark' + (tmpProject ? ' has-sticky-option' : ''),
			placeholder: 'Search by projects...',
			ElementID: 'SampleSearch-Projects',
			ParentID: 'SampleSearch-SearchEntry',
			URL: 'Projects',
			IDField: 'IDProject',
			TextField: 'Name',
			StickyOption: tmpProject,
		}, () =>
		{
			this.log.info('Select2 Projects initialized');
		});

		// MATERIAL Selector Filter
		this.initializeSelect2Plugin({
			IDCustomer: this.CurrentSearchFilters?.IDCustomer,
			placeholder: 'Search by materials..',
			ElementID: 'SampleSearch-Materials',
			ParentID: 'SampleSearch-SearchEntry',
			URL: 'Materials',
			IDField: 'IDMaterial',
			TextField: 'Name',
		}, () =>
		{
			this.log.info('Select2 Materials initialized');
		});

		// MIX DESIGN Selector Filter
		this.initializeSelect2Plugin({
			IDCustomer: this.CurrentSearchFilters?.IDCustomer,
			placeholder: 'Search by mix designs...',
			ElementID: 'SampleSearch-MixDesigns',
			ParentID: 'SampleSearch-SearchEntry',
			URL: 'MixDesigns',
			IDField: 'IDMixDesign',
			TextField: 'MixID',
		}, () =>
		{
			this.log.info('Select2 Mix Designs initialized');
		});

		// START DATE date picker
		window.flatpickr('#SampleSearch-StartDate', {
			dateFormat: 'Y-m-d',
			onReady: function(dateObj, dateStr, instance)
			{
				_Pict.providers.UtilityProvider.buildFlatpickrExtraButtons(instance, 'SampleSearch-StartDate');
			},
			onChange: function(selectedDates, dateStr, instance)
			{
				// a date was selected, update the End Date picker to disable dates before the start date
				/** @type {any}  silence _flatpickr error */
				const siblingElement = document.getElementById('SampleSearch-EndDate');
				siblingElement._flatpickr.config.minDate = dateStr ? dateStr : null;
				siblingElement._flatpickr.redraw();
			},
		});

		// END DATE date picker
		window.flatpickr('#SampleSearch-EndDate', {
			dateFormat: 'Y-m-d',
			onReady: function(dateObj, dateStr, instance)
			{
				_Pict.providers.UtilityProvider.buildFlatpickrExtraButtons(instance, 'SampleSearch-EndDate');
			},
			onChange: function(selectedDates, dateStr, instance)
			{
				// a date was selected, update the Start Date picker to disable dates after the end date
				/** @type {any}  silence _flatpickr error */
				const siblingElement = document.getElementById('SampleSearch-StartDate');
				siblingElement._flatpickr.config.maxDate = dateStr ? dateStr : null;
				siblingElement._flatpickr.redraw();
			},
		});

		this.setCurrentSearchFilters();

		// if there is a callback function, call it
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * This method performs a search based on the current search filters.
	 * @param {Event} event - The event that triggered the search.
	 * @return {Promise<void>}
	 * @async
	 */
	async handleSampleSearch(event)
	{
		// prevent the browser from refreshing on form submissions
		if (event)
		{
			event.preventDefault();
		}
		// add the loading spinner on the button when the action is being performed
		this.pict.ContentAssignment.addClass('#SampleSearch-SearchButton', 'is-loading');
		// simulate that data is loading in the results table
		this.pict.views.HeadlightPanels.addLoadingSkeletonRows('SampleSearch-Results-Container', this.CurrentSearchFilters.PageSize, '2.5rem');
		// collapse the filter panel to get out the way of the results
		this.pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup('SampleSearchFilters', 'close');
		// perform the search
		const tmpSearchResults = await this.pict.providers.SampleProvider.performSampleSearch(this.CurrentSearchFilters);
		// update the search results/UI based on the search results and total search count passed in
		this.updateWithSearchResults(tmpSearchResults.results, tmpSearchResults.count);
	}

	/**
	 * This function is used to handle the selection of a sample from the search results.
	 * @param {Event} event - The event that triggered the selection
	 * @param {string} pIDSample - The ID of the sample that was selected
	 * @param {object} pContext - The context of the sample that was selected
	 * @return {void}
	 */
	handleSampleSelection(event, pIDSample, pContext)
	{
		if (!pIDSample)
		{
			this.log.error('No IDSample provided');
			return;
		}
		if (event)
		{
			event.preventDefault();
			if (event.target instanceof HTMLElement)
			{
				// add loading class to button if it exists
				// TODO: probably should have a "find the parents until the button" function to handle this
				if (event.target?.classList?.contains('button'))
				{
					event.target.classList.add('is-loading');
				}
				else if (event.target?.parentElement?.classList?.contains('button'))
				{
					event.target.parentElement.classList.add('is-loading');
				}
				else if (event.target?.parentElement?.parentElement?.classList?.contains('button'))
				{
					event.target.parentElement.parentElement.classList.add('is-loading');
				}
				else
				{
					this.log.info('No button found to add loading class to');
				}
			}
		}
		// handle the context of the sample selection
		if (pContext === 'Add')
		{
			if (this.pict.views.SampleSearch.sampleSelectionCallback)
			{
				if (this.pict.views.SampleSearch.CurrentSearchResults.length > 0)
				{
					const tmpSample = this.pict.views.SampleSearch.CurrentSearchResults.find((pSample) => pSample.IDSample == pIDSample);
					this.pict.views.SampleSearch.sampleSelectionCallback(tmpSample);
					this.pict.views.HeadlightPanels.handlePanelToggle('Samples', 'close');
				}
			}
		}
		else if (pContext === 'Details')
		{
			this.pict.views.SampleDetails.showSampleDetailView(pIDSample, null, 'Details');
		}
		else
		{
			this.log.error('Unknown context for handleSampleSelection()!', { pContext });
		}
	}

	/**
	 * This method initializes the select2 plugin.
	 * @param {Object} pOptions - The options to use for initialization.
	 * @param {Function} pCallback - The callback function to execute after initialization.
	 * @return {void}
	 */
	initializeSelect2Plugin(pOptions, pCallback)
	{
		$('#' + pOptions.ElementID).select2({
			dropdownParent: $('#' + pOptions.ParentID),
			dropdownCssClass: pOptions.dropdownCssClass || 'theme-dark',
			/* @ts-ignore */
			selectionCssClass: pOptions.selectionCssClass || 'theme-dark',
			delay: pOptions.delay || 250,
			placeholder: pOptions.placeholder || 'Select...',
			cache: true,
			minimumInputLength: pOptions.minimumInputLength > -1 ? pOptions.minimumInputLength : this.pict.views.HeadlightPanels.MinimumInputLengthForSearch,
			language: {
				searching: function()
				{
					return pOptions.searchingMessage || 'Searching...';
				},
				noResults: function()
				{
					return pOptions.noResultsMessage || 'No results found.';
				},
				loadingMore: function()
				{
					return pOptions.loadingMoreMessage || 'Loading more results...';
				},
				inputTooShort: function(args)
				{
					return pOptions.inputTooShortMessage || 'Please enter at least ' + args.minimum + ' characters to see results.';
				},
			},
			ajax: {
				xhrFields: {
					withCredentials: true,
				},
				dataType: 'json',
				method: 'GET',
				url: function(params)
				{
					const tmpSearchQuery = {
						filters: [ {
							field: 'IDCustomer',
							comparator: 'EQ',
							target: pOptions.IDCustomer || 0,
						} ],
						pagination: {
							start: pOptions?.CurrentPage || 1,
							limit: pOptions?.PageSize || 25,
						},
					};
					// add the search term to the query if it exists
					if (params.term)
					{
						tmpSearchQuery.filters.push({
							field: pOptions.TextField,
							comparator: 'LK',
							target: params.term,
						});
					}
					const startCount = (tmpSearchQuery.pagination.start - 1) * tmpSearchQuery.pagination.limit;
					const endCount = tmpSearchQuery.pagination.start * tmpSearchQuery.pagination.limit;
					return `/1.0/${pOptions.URL}/${_Pict.providers.UtilityProvider.generateMeadowFilter(tmpSearchQuery)}/${startCount}/${endCount}`;
				},
				processResults: function(data, params)
				{
					params.page = params.page || 1;

					const tmpSelections = _Pict.views.SampleSearch.CurrentSearchFilters[pOptions.URL] || [];

					return {
						results: _Pict.providers.UtilityProvider.formatSelect2Results(data, pOptions.IDField, pOptions.TextField, tmpSelections, pOptions.StickyOption),
						more: params.term ? data.length === _Pict.views.SampleSearch.CurrentSearchFilters.PageSize : data.length === _Pict.views.SampleSearch.CurrentSearchFilters.PageSize,
					};
				},
			},
		});
		// call the callback function if it exists
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * This method renders the previous page of search results.
	 * @return {void}
	 */
	renderPreviousPage()
	{
		if (this.CurrentSearchFilters.CurrentPage > 1)
		{
			this.CurrentSearchFilters.CurrentPage--;
		}
		else
		{
			this.CurrentSearchFilters.CurrentPage = 1;
		}
		this.pict.views.SampleSearch.handleSampleSearch();
	}

	/**
	 * This method renders the next page of search results.
	 * @return {void}
	 */
	renderNextPage()
	{
		if (this.CurrentSearchFilters.CurrentPage < this.CurrentSearchFilters.TotalPages)
		{
			this.CurrentSearchFilters.CurrentPage++;
		}
		else
		{
			this.CurrentSearchFilters.CurrentPage = this.CurrentSearchFilters.TotalPages;
		}
		this.pict.views.SampleSearch.handleSampleSearch();
	}

	/**
	 * This method resets the search filters to an empty state.
	 * @return {void}
	 */
	resetSearchFilters()
	{
		// reset the search filters to the default state
		this.CurrentSearchFilters = JSON.parse(JSON.stringify(this.DefaultSearchFilters));
		this.setCurrentSearchFiltersCount();
		this.renderAsync(() =>
		{
			this.log.info('Search filters reset');
		});
	}

	/**
	 * This method initializes the view and sets the default search filters.
	 * @param {object} pSearchFilters - The search filters to set.
	 * @return {void}
	 * @example
	 * setCurrentSearchFilters({Projects: [1234], Materials: [5678]})
	 */
	setCurrentSearchFilters(pSearchFilters)
	{
		// set the search filters to the passed in filters
		if (pSearchFilters)
		{
			this.CurrentSearchFilters = Object.assign(JSON.parse(JSON.stringify(this.DefaultSearchFilters)), pSearchFilters);
		}
		// if the search entry form exists (is actively the rendered panel), stuff the values into the form
		if (document.getElementById('SampleSearch-SearchEntry'))
		{
			// convert the date strings to just the date part (we expect ISO strings)
			const tmpStartDate = this.CurrentSearchFilters?.StartDate ? this.CurrentSearchFilters?.StartDate.split('T')[0] : '';
			const tmpEndDate = this.CurrentSearchFilters?.EndDate ? this.CurrentSearchFilters?.EndDate.split('T')[0] : '';
			// for each filter, also stuff the values into the search form.
			this.pict.ContentAssignment.assignContent('#SampleSearch-CurrentPage', this.CurrentSearchFilters?.CurrentPage || 1);
			this.pict.ContentAssignment.assignContent('#SampleSearch-PageSize', this.CurrentSearchFilters?.PageSize || 25);
			this.pict.ContentAssignment.assignContent('#SampleSearch-Projects', this.CurrentSearchFilters?.Projects);
			this.pict.ContentAssignment.assignContent('#SampleSearch-Materials', this.CurrentSearchFilters?.Materials);
			this.pict.ContentAssignment.assignContent('#SampleSearch-MixDesigns', this.CurrentSearchFilters?.MixDesigns);
			this.pict.ContentAssignment.assignContent('#SampleSearch-StartDate', tmpStartDate);
			this.pict.ContentAssignment.assignContent('#SampleSearch-EndDate', tmpEndDate);
			this.pict.ContentAssignment.assignContent('#SampleSearch-SortBy', `${this.CurrentSearchFilters?.SortBy}_${this.CurrentSearchFilters?.SortDirection}`);
		}
		this.setCurrentSearchFiltersCount();
	}

	/**
	 * This method sets the current search filters count on the 'Set Filters' button.
	 * @return {void}
	 */
	setCurrentSearchFiltersCount()
	{
		this.CurrentSearchFiltersCount = 0;
		const my = this.CurrentSearchFilters;

		if (my.Projects && my.Projects.length > 0)
		{
			this.CurrentSearchFiltersCount += my.Projects.length;
		}
		if (my.SearchText && my.SearchText.length > 0)
		{
			this.CurrentSearchFiltersCount += 1;
		}
		if (my.Materials && my.Materials.length > 0)
		{
			this.CurrentSearchFiltersCount += my.Materials.length;
		}
		if (my.MixDesigns && my.MixDesigns.length > 0)
		{
			this.CurrentSearchFiltersCount += my.MixDesigns.length;
		}
		if (my.StartDate)
		{
			this.CurrentSearchFiltersCount += 1;
		}
		if (my.EndDate)
		{
			this.CurrentSearchFiltersCount += 1;
		}
	}

	/**
	 * Set the callback function for the search results.
	 * @param {Function} pCallback - The callback function to set.
	 * @return {void}
	 * @example
	 * setSampleSearchResultsCallback((pResults) => { console.log(pResults); });
	 */
	setSampleSearchResultsCallback(pCallback)
	{
		// TODO: not sure if this is the best way to handle this, but it works for now.
		this.sampleSearchResultsCallback = pCallback;
	}


	/**
	 * Updates the search filter value based on the event and filter type.
	 * @param {string} pFilterType - The type of filter to update.
	 * @return {void}
	 * @example
	 * updateSearchFilterValue('Projects');
	 */
	updateSearchFilterValue(pFilterType)
	{
		switch (pFilterType)
		{
		case 'Project':
		case 'Projects':
			this.CurrentSearchFilters.Projects = this.pict.ContentAssignment.readContent('#SampleSearch-Projects');
			break;
		case 'Material':
		case 'Materials':
			this.CurrentSearchFilters.Materials = this.pict.ContentAssignment.readContent('#SampleSearch-Materials');
			break;
		case 'MixDesign':
		case 'MixDesigns':
			this.CurrentSearchFilters.MixDesigns = this.pict.ContentAssignment.readContent('#SampleSearch-MixDesigns');
			break;
		case 'BeginDate':
		case 'StartDate':
			const tmpStartDate = this.pict.ContentAssignment.readContent('#SampleSearch-StartDate');
			this.CurrentSearchFilters.StartDate = tmpStartDate ? tmpStartDate + 'T00:00:00.000Z' : '';
			break;
		case 'EndDate':
		case 'StopDate':
			const tmpEndDate = this.pict.ContentAssignment.readContent('#SampleSearch-EndDate');
			this.CurrentSearchFilters.EndDate = tmpEndDate ? tmpEndDate + 'T23:59:59.000Z' : '';
			break;
		case 'SortBy':
			const tempSortBy = this.pict.ContentAssignment.readContent('#SampleSearch-SortBy');
			this.CurrentSearchFilters.SortBy = typeof tempSortBy == 'string' ? tempSortBy.split('_')[0] : 'CreateDate';
			this.CurrentSearchFilters.SortDirection = typeof tempSortBy == 'string' ? tempSortBy.split('_')[1] : 'DESC';
			break;
		default:
			this.log.error(`Unknown filter type: ${pFilterType}`);
		}
		this.setCurrentSearchFiltersCount();
	}

	/**
	 * This method updates the search results/UI based on the search results and total search count passed in.
	 * @param {Array} pSearchResults - The search results to update with.
	 * @param {number} pTotalSearchCount - The total search count to update with.
	 * @return {void}
	 */
	updateWithSearchResults(pSearchResults, pTotalSearchCount)
	{
		// update our results list
		this.CurrentSearchResults = pSearchResults;
		// update the total possible count so we can calculate the total pages
		this.CurrentSearchFilters.TotalPages = Math.ceil(pTotalSearchCount / this.CurrentSearchFilters.PageSize);
		// render the search results view
		this.pict.views.SampleSearch.renderAsync('SampleSearch-Results', '#SampleSearch-Results-Container', false, false, () =>
		{
			// remove the loading spinner on the button when the action has been completed
			this.pict.ContentAssignment.removeClass('#SampleSearch-SearchButton', 'is-loading');
			// render the pagination buttons
			this.pict.views.SampleSearch.renderAsync('SampleSearch-Pagination', '#SampleSearch-Pagination-Container', false, false, () =>
			{
				this.log.info('Sample Search Pagination rendered');
			});
			// if there is a callback function, call it
			if (this.sampleSearchResultsCallback)
			{
				this.sampleSearchResultsCallback(this.CurrentSearchResults);
			};
		});
	}
}

module.exports = SampleSearch;

module.exports.default_configuration =
{
	ViewIdentifier: 'SampleSearch',
	DefaultRenderable: 'SampleSearch-Entry',
	DefaultDestinationAddress: '#SampleSearch-Entry-Container',
	Templates:
		[
			{
				Hash: 'SampleSearch-Entry',
				Template: /*html*/`
				<div id="SampleSearch-SearchEntry" class="container">
					<form autocomplete="off" role="sample-search" onsubmit="_Pict.views.SampleSearch.handleSampleSearch(event);">
						<div class="px-4 pt-4" data-collapsible-content="SampleSearchFilters">
							<div class="columns mb-0 is-hidden">
								{~T:SampleSearch-Entry-CurrentPage~}
								{~T:SampleSearch-Entry-PageSize~}
							</div>
							<div class="columns mb-0">
								{~T:SampleSearch-Entry-ProjectSelector~}
							</div>
							<div class="columns mb-0">
								{~T:SampleSearch-Entry-MaterialSelector~}
							</div>
							<div class="columns mb-0">
								{~T:SampleSearch-Entry-MixDesignSelector~}
							</div>
							<div class="columns mb-0">
								{~T:SampleSearch-Entry-StartDate~}
								{~T:SampleSearch-Entry-EndDate~}
								{~T:SampleSearch-Entry-SortBy~}
							</div>
						</div>
						<div class="buttons p-4 is-right" data-theme="dark">
							<div id="SampleSearch-Pagination-Container" style="flex:auto"></div>
							<button 
								id="SampleSearch-ToggleButton"
								type="button"
								title="Toggle filters" 
								class="button is-primary is-soft" 
								data-collapsible-toggle="SampleSearchFilters" 
								onclick="_Pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup('SampleSearchFilters')"
							>
								<span class="icon">
									<i class="fas fa-fw fa-filter"></i>
								</span>
								<span>Set Filters</span>
								<!-- hide the badge until we fix the count to be accurate -->
								<span id="SampleSearch-Entry-ActiveFilterCount" class="is-hidden badge is-warning">{~T:SampleSearch-Entry-ActiveFilterCount~}</span>
							</button>
							<button class="button is-primary is-soft" type="button" title="Reset search" id="SampleSearch-ResetButton" onclick="_Pict.views.SampleSearch.resetSearchFilters()"><i class="fas fa-fw fa-undo mr-1"></i> Clear Filters</button>
							<button class="button is-primary" type="submit" title="Search samples" id="SampleSearch-SearchButton"><i class="fas fa-fw fa-search mr-1"></i> Search Samples</button>
						</div>
					</form>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Entry-ActiveFilterCount',
				Template: /*html*/`{~D:Context[0].CurrentSearchFiltersCount~}`,
			},
			{
				Hash: 'SampleSearch-Entry-CurrentPage',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="SampleSearch-CurrentPage" class="block">Current Page</label>
						<input 
							id="SampleSearch-CurrentPage" 
							type="number" 
							class="input" 
							placeholder="1" 
							onchange="_Pict.views.SampleSearch.updateSearchFilterValue('CurrentPage')" 
							value="" 
						/>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Entry-PageSize',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="SampleSearch-PageSize" class="block">Page Size</label>
						<input 
							id="SampleSearch-PageSize" 
							type="number" 
							class="input" 
							placeholder="50" 
							onchange="_Pict.views.SampleSearch.updateSearchFilterValue('PageSize')" 
							value="" 
						/>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Entry-ProjectSelector',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label block" for="SampleSearch-Projects">Projects</label>
						<div class="control select is-fullwidth">
							<select 
								id="SampleSearch-Projects" 
								onchange="_Pict.views.SampleSearch.updateSearchFilterValue('Projects')"
								multiple
							>{~TS:SampleSearch-Entry-ProjectSelector-SelectedOptions:Context[0].CurrentSearchFilters.Projects~}</select>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Enty-ProjectSelector-SelectedOptions',
				Template: /*html*/`<option value="{~D:Record.IDProject~}" selected>{~D:Record.Name~}</option>`,
			},
			{
				Hash: 'SampleSearch-Entry-MaterialSelector',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label block" for="SampleSearch-Materials">Materials</label>
						<div class="control select is-fullwidth">
							<select 
								id="SampleSearch-Materials" 
								onchange="_Pict.views.SampleSearch.updateSearchFilterValue('Materials')"
								multiple
							>{~TS:SampleSearch-Entry~MaterialSelector-SelectedOptions:Context[0].CurrentSearchFilters.Materials~}</select>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Entry-MaterialSelector-SelectedOptions',
				Template: /*html*/`<option value="{~D:Record.IDMaterial~}" selected>{~D:Record.Name~}</option>`,
			},
			{
				Hash: 'SampleSearch-Entry-MixDesignSelector',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label block" for="SampleSearch-MixDesigns">Mix Designs</label>
						<div class="control select is-fullwidth">
							<select 
								id="SampleSearch-MixDesigns" 
								onchange="_Pict.views.SampleSearch.updateSearchFilterValue('MixDesigns')"
								multiple
							>{~TS:SampleSearch-Entry~MixDesignSelector-SelectedOptions:Context[0].CurrentSearchFilters.MixDesigns~}</select>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Entry-MixDesignSelector-SelectedOptions',
				Template: /*html*/`<option value="{~D:Record.IDMixDesign~}" selected>{~D:Record.MixID~}</option>`,
			},
			{
				Hash: 'SampleSearch-Entry-StartDate',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="SampleSearch-StartDate" class="block">Start Date</label>
						<div class="control has-icons-left">
							<input 
								id="SampleSearch-StartDate" 
								type="date" 
								class="input" 
								placeholder="Search by Start Date..." 
								onchange="_Pict.views.SampleSearch.updateSearchFilterValue('StartDate')"
								value="" 
							/>
							<span class="icon is-small is-left">
								<i class="fas fa-calendar fa-xs"></i>
							</span>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Entry-EndDate',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="SampleSearch-EndDate" class="block">End Date</label>
						<div class="control has-icons-left">
							<input 
								id="SampleSearch-EndDate" 
								type="date" 
								class="input" 
								placeholder="Search by End Date..." 
								onchange="_Pict.views.SampleSearch.updateSearchFilterValue('EndDate')"
								value="" 
							/>
							<span class="icon is-small is-left">
								<i class="fas fa-calendar fa-xs"></i>
							</span>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Entry-SortBy',
				Template: /*html*/`
				<div class="column">
					<div class="field">
						<label class="label" for="SampleSearch-SortBy" class="block">Sort By</label>
						<div class="control select is-fullwidth">
							<select 
								id="SampleSearch-SortBy" 
								type="date" 
								class="input" 
								placeholder="Search by End Date..." 
								onchange="_Pict.views.SampleSearch.updateSearchFilterValue('SortBy')"
								value="" 
							>
								<option value="CreateDate_DESC">Create Date (Newest)</option>
								<option value="CreateDate_ASC">Create Date (Oldest)</option>
								<option value="Status_ASC">Status (A-Z)</option>
								<option value="Status_DESC">Status (Z-A)</option>
							</select>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Results',
				Template: /*html*/`
				<div id="SampleSearch-Results-Container" class="container table-container">
					{~TIfAbs:SampleSearch-Results-Empty:Context[0]:Context[0].CurrentSearchResults.length^==^0~}
					{~TIfAbs:SampleSearch-Results-Table:Context[0]:Context[0].CurrentSearchResults.length^>^0~}
				</div>`,
			},
			{
				Hash: 'SampleSearch-Results-Table',
				Template: /*html*/`
				<table class="table is-striped is-hoverable is-fullwidth has-sticky-table-header">
					<thead>
						<tr>
							<th>Create Date</th>
							<th>Project</th>
							<th>Material</th>
							<th>Mix Design</th>
							<th class="has-text-centered">Status</th>
							<th class="has-text-centered">Details</th>
							{~T:SampleSearch-Results-Extra-Header~}
						</tr>
					</thead>
					<tbody id="SampleSearch-Results-Entries">
						{~TS:SampleSearch-Results-Row:Record.CurrentSearchResults~}
					</tbody>
				</table>`,
			},
			{
				Hash: 'SampleSearch-Results-Row',
				Template: /*html*/`
				<tr>
					<td>{~DateFormat:Record.CreateDate^YYYY-MM-DD~}</td>
					<td>
						{~TIfAbs:SampleSearch-Results-EntityValue-Project:Record:Record.IDProject^>^0~}
						{~TIfAbs:SampleSearch-Results-EntityValue-NA:Record:Record.IDProject^<=^0~}
					</td>
					<td>
						{~TIfAbs:SampleSearch-Results-EntityValue-Material:Record:Record.IDMaterial^>^0~}
						{~TIfAbs:SampleSearch-Results-EntityValue-NA:Record:Record.IDMaterial^<=^0~}
					</td>
					<td>
						{~TIfAbs:SampleSearch-Results-EntityValue-MixDesign:Record:Record.IDMixDesign^>^0~}
						{~TIfAbs:SampleSearch-Results-EntityValue-NA:Record:Record.IDMixDesign^<=^0~}
					</td>
					<td class="has-text-centered">
						{~StatusPill:Record.Status~}
					</td>
					<td class="has-text-centered is-vcentered">
						<button type="button" class="button is-ghost has-text-primary-75 p-0" title="See Details" onclick="_Pict.views.SampleSearch.handleSampleSelection(event, '{~D:Record.IDSample~}', 'Details')">
							<span class="icon">
								<i class="fas fa-fw fa-lg fa-circle-ellipsis"></i>
							</span>
						</button>
					</td>
					{~T:SampleSearch-Results-Extra-Column:Record~}
				</tr>`,
			},
			{
				Hash: 'SampleSearch-Results-Extra-Header',
				Template: /*html*/`<th class="has-text-centered" data-panel-mode-show-if="add-to-set">Select</th>`,
			},
			{
				Hash: 'SampleSearch-Results-Extra-Column',
				Template: /*html*/`
				<td class="has-text-centered is-vcentered" data-panel-mode-show-if="add-to-set">
					<button type="button" class="button is-ghost has-text-primary-75 p-0" title="Select Sample" onclick="_Pict.views.SampleSearch.handleSampleSelection(event, '{~D:Record.IDSample~}', 'Add')">
						<span class="icon">
							<i class="fa fa-fw fa-lg fa-plus-circle"></i>
						</span>
					</button>
				</td>`,
			},
			{
				Hash: 'SampleSearch-Results-EntityValue-NA',
				Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
			},
			{
				Hash: 'SampleSearch-Results-DatumValue-NA',
				Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
			},
			{
				Hash: 'SampleSearch-Results-EntityValue-Project',
				Template: /*html*/`{~Entity:Project^Record.IDProject^SampleSearch-Results-Project^~}`,
			},
			{
				Hash: 'SampleSearch-Results-EntityValue-Material',
				Template: /*html*/`{~Entity:Material^Record.IDMaterial^SampleSearch-Results-Material^~}`,
			},
			{
				Hash: 'SampleSearch-Results-EntityValue-MixDesign',
				Template: /*html*/`{~Entity:MixDesign^Record.IDMixDesign^SampleSearch-Results-MixDesign^~}`,
			},
			{
				Hash: 'SampleSearch-Results-DatumValue-Status',
				Template: /*html*/`{~D:Record.Status~}`,
			},
			{
				Hash: 'SampleSearch-Results-Empty',
				Template: /*html*/`
				<div class="notification m-3 empty-message">
					<p class="has-text-centered">
						<span class="icon is-large">
							<i class="fas fa-search"></i>
						</span>
						<span>We could not find any samples based on the current search criteria.</span>
					</p>
				</div>`,
			},
			{
				Hash: 'SampleSearch-Results-Material',
				Template: /*html*/`{~D:Record.Name~}`,
			},
			{
				Hash: 'SampleSearch-Results-MixDesign',
				Template: /*html*/`{~D:Record.MixID~}`,
			},
			{
				Hash: 'SampleSearch-Results-Project',
				Template: /*html*/`{~D:Record.Name~}`,
			},
			{
				Hash: 'SampleSearch-Pagination',
				Template: /*html*/`
				<nav id="SampleSearch-Pagination" class="pagination" style="justify-content: flex-start;" role="navigation" aria-label="pagination">
					<button id="SampleSearch-PreviousButton" type="button" title="Previous page" class="button is-soft mr-2" onclick="_Pict.views.SampleSearch.renderPreviousPage();">
						<span class="icon">
							<i class="fas fa-fw fa-chevron-left"></i> 
						</span>
						<span>Previous</span>
					</button>
					<button id="SampleSearch-NextButton" type="button" title="Next page" class="button is-soft" onclick="_Pict.views.SampleSearch.renderNextPage();">
						<span>Next</span> 
						<span class="icon">
							<i class="fas fa-fw fa-chevron-right ml-1"></i>
						</span>
					</button>
				</nav>`,
			},
		],
	Renderables:
		[
			{
				RenderableHash: 'SampleSearch-Entry',
				TemplateHash: 'SampleSearch-Entry',
				DestinationAddress: '#SampleSearch-Entry-Container',
			},
			{
				RenderableHash: 'SampleSearch-Entry-ProjectSelector',
				TemplateHash: 'SampleSearch-Entry-ProjectSelector',
				DestinationAddress: '#SampleSearch-Entry-ProjectSelector',
			},
			{
				RenderableHash: 'SampleSearch-Entry-MaterialSelector',
				TemplateHash: 'SampleSearch-Entry-MaterialSelector',
				DestinationAddress: '#SampleSearch-Entry-MaterialSelector',
			},
			{
				RenderableHash: 'SampleSearch-Entry-MixDesignSelector',
				TemplateHash: 'SampleSearch-Entry-MixDesignSelector',
				DestinationAddress: '#SampleSearch-Entry-MixDesignSelector',
			},
			{
				RenderableHash: 'SampleSearch-Entry-ActiveFilterCount',
				TemplateHash: 'SampleSearch-Entry-ActiveFilterCount',
				DestinationAddress: '#SampleSearch-Entry-ActiveFilterCount',
			},
			{
				RenderableHash: 'SampleSearch-Results',
				TemplateHash: 'SampleSearch-Results',
				DestinationAddress: '#SampleSearch-Results-Container',
			},
			{
				RenderableHash: 'SampleSearch-Pagination',
				TemplateHash: 'SampleSearch-Pagination',
				DestinationAddress: '#SampleSearch-Pagination-Container',
			},
		],
};
